import { _axios } from "../axios";
import { AxiosResponse, AxiosPromise } from "axios";

/**
 * The resource name
 * @type {string}
 */
const resource = '/products';

/**
 * Gets all products
 *
 * @returns {AxiosResponse<any>}
 */
export function getAllProducts() {
    return _axios.get( resource );
}

/**
 * Returns one product based on the slug
 *
 * @param productSlug
 *
 * @returns {AxiosPromise<any>}
 */
export function getProduct( productSlug = '' ) {
    return _axios.get( resource + '/' + productSlug )
}

/**
 * Searches products, this one comes with a can
 *
 * @param searchTerm
 * @param cancelToken
 *
 * @returns {AxiosPromise<any>}
 */
export function searchProducts( searchTerm = '', cancelToken ) {
    return _axios.get( resource + '/search/' + searchTerm, { cancelToken: cancelToken } );
}

/**
 * Returns the parent product
 *
 * @param productSlug
 *
 * @returns {AxiosResponse<any>}
 */
export function getProductParent( productSlug = '' ) {
    return _axios.get( resource + '/' + productSlug + '/parent' );
}

/**
 * Gets the stock status of the product
 *
 * @param productSlug
 * @returns {AxiosResponse<any>}
 */
export function getProductStockStatus( productSlug = '' ) {
    return _axios.get( resource + '/' + productSlug + '/status' );
}

/**
 * Updates the product in the store
 *
 * @param productSlug
 * @param product
 * @returns {AxiosResponse<any>}
 */
export function updateProduct( productSlug = '', product = {} ) {
    return _axios.put( resource + '/' + productSlug, product );
}

/**
 * Saves the product to the store
 *
 * @param product
 * @returns {AxiosResponse<any>}
 */
export function storeProduct( product = {} ) {
    return _axios.post( resource, product );
}

/**
 * Deletes the product
 *
 * @param productSlug
 * @returns {AxiosResponse<any>}
 */
export function destroyProduct( productSlug = '' ) {
    return _axios.delete( resource + '/' + productSlug );
}

/**
 * Gets sales data for a product in a given time frame
 *
 * @param productSlug
 * @param startDate
 * @param endDate
 * @returns {AxiosResponse<any>}
 */
export function getProductSales( productSlug = '', startDate = null, endDate = null ) {
    let urlString = resource + '/' + productSlug + '/sales';
    urlString += ( null !== startDate ) ? '/' + startDate : '';
    urlString += ( null !== endDate ) ? '/' + endDate : '';

    return _axios.get( urlString );
}

/**
 * Gets the raw sales total for a given product in a given time frame
 *
 * @param productSlug
 * @param startDate
 * @param endDate
 * @returns {AxiosResponse<any>}
 */
export function getProductSalesTotal( productSlug = '', startDate = null, endDate = null ) {
    let urlString = resource + '/' + productSlug + '/sales-total';
    urlString += ( null !== startDate ) ? '/' + startDate : '';
    urlString += ( null !== endDate ) ? '/' + endDate : '';

    return _axios.get( urlString );
}

export function getProductStockOrders( productSlug = '', startDate = null, endDate = null ) {
    let urlString = resource + '/' + productSlug + '/stock-orders';
    urlString += ( null !== startDate ) ? '/' + startDate : '';
    urlString += ( null !== endDate ) ? '/' + endDate : '';
    
    return _axios.get( urlString );
}

/**
 * Get the raw ordered total for a given product in a given time frame
 *
 * @param productSlug
 * @param startDate
 * @param endDate
 * @returns {AxiosResponse<any>}
 */
export function getProductOrderedTotal( productSlug = '', startDate = null, endDate = null) {
    let urlString = resource + '/' + productSlug + '/ordered-total';
    urlString += ( null !== startDate ) ? '/' + startDate : '';
    urlString += ( null !== endDate ) ? '/' + endDate : '';

    return _axios.get( urlString );
}

/**
 * Updates the current stock of a product
 *
 * @param productSlug
 * @param quantity
 * @returns {AxiosResponse<any>}
 */
export function updateProductStock( productSlug = '', quantity = 0 ) {
    return _axios.put( resource + '/' + productSlug + '/stock-update', { 'quantity' : quantity } );
}

/**
 *
 * @param searchTerm
 * @returns{AxiosResponse<any>}
 */
export function searchProductsStock(searchTerm = '') {
    return _axios.get(resource + '/search-stock/' + searchTerm);
}

/**
 * Uploads photos
 *
 * @param formData
 * @returns{AxiosResponse<any>}
 */
export function uploadProductPhoto( formData = new FormData() ) {
    return _axios.post(resource + '/photos/upload', formData );
}

/**
 * Gets all unlinked products
 *
 * @returns {AxiosResponse<T>}
 */
export function getUnlinkedProducts() {
    return _axios.get(resource + '/unlinked');
}

/**
 * Gets all parent products without department
 *
 * @returns {AxiosResponse<T>}
 */
export function getProductsNotInDepartment() {
    return _axios.get( resource + '/no-department');
}

/**
 * Gets all parent products without department
 *
 * @returns {AxiosResponse<T>}
 */
export function getProductsNotCounted() {
    return _axios.get( resource + '/not-counted');
}

/**
 * Gets all products with multiple suppliers so we can set the pref.
 *
 * @returns {Promise<AxiosResponse<any>>}
 */
export function getProductsWithMultipleSuppliers() {
    return _axios.get(`${resource}/multi-suppliers`);
}

/**
 * Get all archived products
 *
 * @returns {AxiosResponse<T>}
 */
export function getArchivedProducts() {
    return _axios.get( resource + '/archived');
}

/**
 * Gets all products that have never been ordered
 *
 * @returns {AxiosResponse<T>}
 */
export function getUnorderedProducts() {
    return _axios.get(resource + '/unordered');
}

/**
 * Gets all products that have never been ordered but have been sold
 *
 * @returns {AxiosResponse<T>}
 */
export function getUnorderedSoldProducts() {
    return _axios.get(resource + '/unordered-sold');
}

/**
 * Restore a product
 *
 * @param slug
 * @returns {AxiosResponse<T>}
 */
export function restoreProduct(slug) {
    return _axios.get( `${resource}/${slug}/restore`);
}

/**
 * Exports a count sheet for a given search term
 *
 * @param searchTerm
 * @returns {Promise<AxiosResponse<any>>}
 */
export function exportSearchCountSheet(searchTerm) {
    return _axios.get( `${resource}/export-search/${searchTerm}`)
}